/**
 * Your data (#51): full team export, closing the team, and deleting your own account.
 * Nothing is destroyed immediately: closed teams and deleted records are kept for the
 * retention period, as the privacy policy explains.
 */
import { v } from "@shared/validation.ts";
import { Alert, Table } from "react-bootstrap";
import { TextField } from "../../components/forms/fields.tsx";
import { ModalFormButton } from "../../components/forms/ModalForm.tsx";
import { Icon } from "../../components/ui/Icon.tsx";
import { QueryState, Section, StatusBadge } from "../../components/ui/kit.tsx";
import { api } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { formatBytes, useFormat } from "../../lib/format.ts";
import { useApi } from "../../lib/query.ts";

interface TeamExport {
  id: string;
  status: "pending" | "running" | "ready" | "failed" | "expired";
  sizeBytes: number | null;
  error: string | null;
  expiresAt: string | null;
  createdAt: string;
  requestedByName: string | null;
}

const passwordSchema = v.object({ password: v.string({ min: 1, max: 200 }) });
const closeSchema = v.object({
  confirmName: v.string({ min: 1, max: 200 }),
  password: v.string({ min: 1, max: 200 }),
});

export function DataSettingsPage() {
  const { me } = useAuth();
  const f = useFormat();
  const exports = useApi<TeamExport[]>("/api/team/exports");
  const teamName = me?.teams.find((t) => t.id === me.activeTeamId)?.name ?? "";
  const isOwner = me?.teams.find((t) => t.id === me.activeTeamId)?.isOwner ?? false;
  return (
    <>
      <Section
        title="Export all your data"
        actions={
          isOwner && (
            <ModalFormButton
              label={
                <>
                  <Icon name="download" size={14} /> Request export
                </>
              }
              title="Export everything in this team"
              schema={passwordSchema}
              defaults={{ password: "" }}
              onSubmit={(val) => api.post("/api/team/exports", val)}
            >
              {() => (
                <>
                  <p className="small">
                    We'll put every record (as spreadsheets and JSON) and every document into one zip file,
                    and email you when it's ready. Enter your password to confirm it's you.
                  </p>
                  <TextField name="password" label="Your password" type="password" required />
                </>
              )}
            </ModalFormButton>
          )
        }
      >
        {!isOwner && (
          <p className="small text-muted">Only an Owner of the team can export all of its data.</p>
        )}
        <QueryState query={exports}>
          {(rows) =>
            rows.length === 0 ? (
              <p className="small text-muted mb-0">No exports yet.</p>
            ) : (
              <Table size="sm" className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Requested</th>
                    <th>Status</th>
                    <th>Size</th>
                    <th>Available until</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => (
                    <tr key={e.id}>
                      <td className="small">
                        {f.dateTime(e.createdAt)}
                        {e.requestedByName ? ` by ${e.requestedByName}` : ""}
                      </td>
                      <td>
                        <StatusBadge status={e.status} />
                      </td>
                      <td className="small">{e.sizeBytes ? formatBytes(e.sizeBytes) : "—"}</td>
                      <td className="small">{e.status === "ready" ? f.dateTime(e.expiresAt) : "—"}</td>
                      <td className="text-end">
                        {e.status === "ready" && (
                          <a
                            className="btn btn-sm btn-outline-primary"
                            href={`/api/team/exports/${e.id}/download`}
                          >
                            Download
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )
          }
        </QueryState>
      </Section>
      {isOwner && (
        <Section title="Close this team">
          <p className="small">
            Closing <strong>{teamName}</strong> removes access for every member straight away and cancels any
            invitations. Your records and documents are kept for 7 years, as our privacy policy explains, and
            then deleted. Export your data first if you want a copy. To reopen a closed team, contact support.
          </p>
          <ModalFormButton
            label="Close team"
            variant="outline-danger"
            title={`Close ${teamName}`}
            schema={closeSchema}
            defaults={{ confirmName: "", password: "" }}
            onSubmit={async (val) => {
              await api.post("/api/team/close", val);
              window.location.assign("/");
            }}
          >
            {() => (
              <>
                <Alert variant="warning" className="small">
                  Every member, including you, loses access to this team.
                </Alert>
                <TextField
                  name="confirmName"
                  label={`Type the team's name (${teamName}) to confirm`}
                  required
                />
                <TextField name="password" label="Your password" type="password" required />
              </>
            )}
          </ModalFormButton>
        </Section>
      )}
    </>
  );
}

/** For the account settings page. */
export function DeleteAccountSection() {
  return (
    <Section title="Delete your account">
      <p className="small">
        You'll be signed out everywhere and won't be able to sign in again. Your name and email address are
        removed. Records you added stay with your teams. If you're the only member of a team, that team is
        closed; if you're its only Owner and it has other members, make someone else an Owner first.
      </p>
      <ModalFormButton
        label="Delete account"
        variant="outline-danger"
        title="Delete your account"
        schema={passwordSchema}
        defaults={{ password: "" }}
        onSubmit={async (val) => {
          await api.post("/api/account/delete", val);
          window.location.assign("/login");
        }}
      >
        {() => <TextField name="password" label="Your password" type="password" required />}
      </ModalFormButton>
    </Section>
  );
}
